/**
 * ATC Station Adapter
 *
 * Adapter that implements StationGateway interface.
 * Wraps existing AtcManifestService for station and scenario data.
 *
 * Clean Architecture:
 * - adapt-gateway-abstraction: Hides CDN manifest details behind domain gateway
 * - dep-interface-ownership: Implements interface owned by domain layer
 * - adapt-anti-corruption-layer: Translates external formats to domain entities
 */

import type { Station as DomainStation } from '@/domain/entities';
import type { StationGateway, FetchStationsResult } from '@/domain/gateways';
import { StationMapper } from '../mappers';
import { AtcManifestService } from '@/app/services/atc/AtcManifestService';
import { AtcManifestMemoryCache } from '@/app/services/atc/AtcManifestMemoryCache';
import { AtcManifestCacheService } from '@/app/services/atc/AtcManifestCacheService';
import { mapApiMainManifestToStations } from '@/app/services/atc/mappers/AtcManifestMapper';
import { Logger } from '@/app/utils/logger';

const LOG_TAG = 'atc-station-adapter';

/**
 * Adapter implementing StationGateway using AtcManifestService.
 *
 * Uses AtcManifestService's three-tier caching:
 * - In-memory cache (fastest)
 * - localStorage cache (persistent)
 * - Network fetch (CDN)
 */
export class AtcStationAdapter implements StationGateway {
  /**
   * Fetch all available ATC stations.
   *
   * @param options.forceRefresh - If true, bypass cache and fetch from network
   * @returns FetchStationsResult with domain Station entities
   */
  async fetchStations(options?: {
    forceRefresh?: boolean;
  }): Promise<FetchStationsResult> {
    const forceRefresh = options?.forceRefresh ?? false;

    Logger.info(`Fetching stations (forceRefresh=${forceRefresh})`, LOG_TAG);

    // Get manifest with source info for metadata
    const manifestResult = await AtcManifestService.fetchMainManifestWithSource(
      forceRefresh
    );

    // Get stations list (transforms API data internally)
    const legacyStations = await AtcManifestService.getAvailableStations(
      forceRefresh
    );

    // Map to domain entities
    const domainStations = legacyStations.map((s) =>
      StationMapper.toDomainStation(s)
    );

    Logger.info(
      `Stations fetched: ${domainStations.length} stations, source=${manifestResult.source}`,
      LOG_TAG
    );

    return {
      stations: domainStations,
      source: manifestResult.source as 'network' | 'cache' | 'fallback',
      isStale: manifestResult.isStale,
    };
  }

  /**
   * Fetch scenarios for a specific station.
   *
   * @param station - Domain station to fetch scenarios for
   * @param forceRefresh - If true, bypass cache and fetch from network
   * @returns Station with scenarios loaded
   */
  async fetchStationScenarios(
    station: DomainStation,
    forceRefresh = false
  ): Promise<DomainStation> {
    Logger.info(
      `Fetching scenarios for ${station.displayName} (forceRefresh=${forceRefresh})`,
      LOG_TAG
    );

    // Convert domain station to legacy format for service call
    const legacyStation = StationMapper.toLegacyStation(station);

    // Load scenarios via AtcManifestService
    const stationWithScenarios = await AtcManifestService.loadStationScenarios(
      legacyStation,
      forceRefresh
    );

    // Map back to domain entity
    const domainStation = StationMapper.toDomainStation(stationWithScenarios);

    Logger.info(
      `Scenarios loaded: ${domainStation.scenarios?.length ?? 0} scenarios for ${station.displayName}`,
      LOG_TAG
    );

    return domainStation;
  }

  /**
   * Get cached stations synchronously from the existing cache tiers.
   *
   * Checks Tier 1 (in-memory) first, then Tier 2 (localStorage).
   * Returns null only when no cached manifest exists in either tier.
   *
   * @returns Cached domain stations, or null if no cache exists
   */
  getCachedStations(): DomainStation[] | null {
    try {
      // Tier 1: In-memory cache (fastest, may be expired — use raw to include stale)
      const memoryCached = AtcManifestMemoryCache.getMainManifest();
      if (memoryCached) {
        const legacyStations = mapApiMainManifestToStations(
          memoryCached.data as Parameters<typeof mapApiMainManifestToStations>[0]
        );
        Logger.debug(
          `getCachedStations: ${legacyStations.length} stations from memory cache`,
          LOG_TAG
        );
        return legacyStations.map((s) => StationMapper.toDomainStation(s));
      }

      // Tier 2: localStorage cache (persistent across sessions)
      const localCached = AtcManifestCacheService.getCachedMainManifest();
      if (localCached) {
        const legacyStations = mapApiMainManifestToStations(
          localCached.data as Parameters<typeof mapApiMainManifestToStations>[0]
        );
        Logger.debug(
          `getCachedStations: ${legacyStations.length} stations from localStorage cache`,
          LOG_TAG
        );
        return legacyStations.map((s) => StationMapper.toDomainStation(s));
      }

      Logger.debug('getCachedStations: no cached manifest found', LOG_TAG);
      return null;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`getCachedStations failed: ${message}`, LOG_TAG);
      return null;
    }
  }

  /**
   * Clear all cached station and scenario data.
   */
  clearCache(): void {
    AtcManifestService.clearCache();
    Logger.info('ATC station cache cleared', LOG_TAG);
  }

  /**
   * Clear cache for a specific station.
   *
   * @param manifestUrl - The station's manifest URL
   */
  clearStationCache(manifestUrl: string): void {
    AtcManifestService.clearStationCache(manifestUrl);
    Logger.info(`Station cache cleared: ${manifestUrl}`, LOG_TAG);
  }

  /**
   * Check if a background refresh is in progress for main manifest.
   */
  isMainManifestRefreshInFlight(): boolean {
    return AtcManifestService.isMainManifestRefreshInFlight();
  }

  /**
   * Check if a background refresh is in progress for a station manifest.
   */
  isStationManifestRefreshInFlight(manifestUrl: string): boolean {
    return AtcManifestService.isStationManifestRefreshInFlight(manifestUrl);
  }
}

// ============================================================================
// Singleton Factory
// ============================================================================

let stationAdapterInstance: AtcStationAdapter | null = null;

/**
 * Get singleton AtcStationAdapter instance.
 */
export function getAtcStationAdapter(): AtcStationAdapter {
  if (!stationAdapterInstance) {
    stationAdapterInstance = new AtcStationAdapter();
  }
  return stationAdapterInstance;
}

/**
 * Reset singleton instance (for testing).
 */
export function resetAtcStationAdapter(): void {
  stationAdapterInstance = null;
}
